import { AnalyticsEvent } from 'utils/analytics/AnalyticsEvent'
import Box from '@mui/material/Box'
import { DetailFileAttachment } from './DetailFileAttachment.component'
import { DownloadAllFilesButton } from './DownloadAllFilesButton.component'
import { FC } from 'react'
import { GRAY_900 } from 'constants/styling/theme'
import { RendererErrorState } from './RendererErrorState.component'
import Stack from '@mui/material/Stack'
import { logAnalyticsEvent } from 'utils/analytics/analyticsUtils'
import { logger } from 'utils/logging'
import { useAssignmentDetailDrawerWorkspaceId } from '../AssignmentDetailDrawer.context'
import { useGalleryAssignment } from 'components/pages/Gallery/_main/contexts/GalleryAssignment.context'
import { UploadedFileDTO, useGetAssignmentUploadedFiles } from 'dataQueries/assignmentDetails.query'
import { useTranslation } from 'react-i18next'

interface Props {
  assignmentId: string
}

/**
 * Self-contained section that fetches and displays uploaded input files for an assignment.
 * Always shows the centered title. Displays a loading spinner, error state with retry, or file list.
 * @example
 * <UploadedFilesSection assignmentId={assignment.id} />
 */
export const UploadedFilesSection: FC<Props> = ({ assignmentId }) => {
  const { t } = useTranslation(['gallery'])
  const workspaceId = useAssignmentDetailDrawerWorkspaceId()
  const { data: files, isLoading, isError, refetch } = useGetAssignmentUploadedFiles(assignmentId, workspaceId)
  const { realtyId } = useGalleryAssignment()

  const handleDownload = (file: UploadedFileDTO) => {
    if (!file.signedUrl.startsWith('https://')) {
      logger.warn('Skipped uploaded-file download due to non-https url', undefined, { context: { component: 'UploadedFilesSection', metadata: { assignmentId, fileName: file.filename, urlPrefix: file.signedUrl.slice(0, 8) } } })
      return
    }

    logAnalyticsEvent(AnalyticsEvent.ASSIGNMENT_DETAIL_DOWNLOAD_FILE, {
      assignmentId,
      realtyId,
      planId: workspaceId,
      fileName: file.filename,
      originalFileName: file.originalFilename,
    })

    window.open(file.signedUrl, '_blank', 'noopener,noreferrer')
  }

  if (isLoading || (!isError && !files?.length)) return null

  return (
    <Stack alignItems="center" paddingBottom="2.4rem">
      <Box
        fontSize="1.6rem"
        fontWeight={600}
        color={GRAY_900}
        marginBottom="1.6rem"
        textAlign="center"
      >
        {t('gallery:assignment_details.uploaded_files')}
      </Box>

      {isError && <RendererErrorState onRetry={refetch} />}

      {!isError && files &&

        <Stack width="100%" gap={0.8}>
          {files.map((file) => (
            <DetailFileAttachment
              key={file.filename}
              fileName={file.originalFilename}
              onDownload={() => handleDownload(file)}
            />
          ))}
        </Stack>
      }

      {!isError && !!files?.length &&

        <Box marginTop="1.6rem">

          <DownloadAllFilesButton
            zipName={assignmentId}
            getFiles={async () => {
              const fresh = await refetch()
              return (fresh.data ?? []).map((file) => ({
                url: file.signedUrl,
                filename: file.originalFilename,
              }))
            }}
          />

        </Box>
      }

    </Stack>
  )
}
